/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { localDate } from "../WorkDay/shared/dates";
import type { LinkView } from "./types";

type Props = { token: string; view: LinkView; onSaved: () => void };

const STATUS: Record<string, string> = {
  pending: "badge-warning badge-outline",
  approved: "badge-success",
  declined: "badge-error badge-outline",
};

const day = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

// Ask for time off; see what's been approved.
export const TimeOff = defineComponent<Props>({
  name: "TimeOff",
  props: ["token", "view", "onSaved"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const start = ref(localDate());
    const end = ref(localDate());
    const reason = ref("");
    const saving = ref(false);

    const submit = async () => {
      saving.value = true;
      try {
        await trpc.teamLink.requestTimeOff.mutate({
          token: props.token,
          start_date: start.value,
          end_date: end.value < start.value ? start.value : end.value,
          reason: reason.value.trim() || null,
        });
        reason.value = "";
        toast.showToast(t("teamlink.page.time_off_sent"), { type: "success" });
        props.onSaved();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("teamlink.page.failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    const cancel = async (id: number) => {
      if (!confirm(t("teamlink.page.cancel_confirm"))) return;
      await trpc.teamLink.cancelTimeOff.mutate({ token: props.token, id });
      props.onSaved();
    };

    return () => (
      <div class="space-y-4">
        <div class="space-y-3 rounded-2xl bg-base-100 p-5">
          <h2 class="text-xl font-semibold">{t("teamlink.page.time_off")}</h2>
          <div class="grid grid-cols-2 gap-3">
            <label class="rounded-xl bg-base-200 p-3">
              <span class="block text-xs text-base-content/60">{t("teamlink.page.start_date")}</span>
              <input type="date" class="w-full bg-transparent text-base font-semibold outline-none" min={localDate()} v-model={start.value} />
            </label>
            <label class="rounded-xl bg-base-200 p-3">
              <span class="block text-xs text-base-content/60">{t("teamlink.page.end_date")}</span>
              <input type="date" class="w-full bg-transparent text-base font-semibold outline-none" min={start.value} v-model={end.value} />
            </label>
          </div>
          <input class="input input-bordered w-full" placeholder={t("teamlink.page.reason_placeholder")} v-model={reason.value} />
          <button class="btn btn-primary btn-block" disabled={saving.value} onClick={submit}>
            {t("teamlink.page.submit_request")}
          </button>
        </div>
        {props.view.time_off.length > 0 && (
          <ul class="divide-y divide-base-200 rounded-2xl bg-base-100">
            {props.view.time_off.map((r) => (
              <li key={r.id} class="flex items-center gap-3 p-4">
                <div class="min-w-0 flex-1">
                  <div class="font-medium">{r.start_date === r.end_date ? day(r.start_date) : `${day(r.start_date)} – ${day(r.end_date)}`}</div>
                  {r.reason && <div class="truncate text-xs text-base-content/60">{r.reason}</div>}
                </div>
                <span class={`badge badge-sm ${STATUS[r.status] ?? "badge-ghost"}`}>{t(`teamlink.page.status_${r.status}`)}</span>
                {r.status === "pending" && (
                  <button class="btn btn-ghost btn-xs" onClick={() => cancel(r.id)}>
                    {t("teamlink.page.cancel")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
});
